import {useState, type FormEvent} from 'react';
import {AwButton, AwCheckbox, AwField, AwLifecycleRail, AwModal, AwPanel, AwSelect, AwStatusPill, type LifecycleStage} from '@ascension-tracker/ascension-ui';
import {
  useEnvironmentChecks,
  useEnvironmentHealth,
  useReleaseReadiness,
  useRunValidation,
  useSafeguards,
  useUpdateEnvironmentCheck,
  useUpdateReadinessCheck,
  useUpdateSafeguards,
  type TaskEnvironment
} from '../hooks/useTasks.js';

const readinessLabels: Record<string, string> = {build_verified: 'Build Verified', automated_tests: 'Automated Tests', manual_qa: 'Manual QA', performance: 'Performance', security_scan: 'Security Scan', release_notes: 'Release Notes'};
const readinessStates = ['pending', 'passed', 'failed'] as const;

export function ReadinessPanel({releaseId}: {releaseId: string}) {
  const readiness = useReleaseReadiness(releaseId);
  const updateCheck = useUpdateReadinessCheck(releaseId);
  const runValidation = useRunValidation(releaseId);
  return (
    <AwPanel>
      <h2>Deployment Readiness</h2>
      <ul className="overview-list">
        {(readiness.data ?? []).map(check => (
          <li key={check.checkKey}>
            <span>{readinessLabels[check.checkKey] ?? check.checkKey}</span>
            <AwStatusPill state={check.state === 'passed' ? 'completed' : check.state === 'failed' ? 'failed' : 'pending'}>{check.state}</AwStatusPill>
            <select aria-label={`Set ${readinessLabels[check.checkKey] ?? check.checkKey}`} value={check.state} onChange={event => updateCheck.mutate({checkKey: check.checkKey, state: event.target.value as typeof readinessStates[number]})}>
              {readinessStates.map(state => <option key={state} value={state}>{state}</option>)}
            </select>
          </li>
        ))}
      </ul>
      <AwButton type="button" variant="secondary" onClick={() => runValidation.mutate()} disabled={runValidation.isPending}>Run Validation</AwButton>
    </AwPanel>
  );
}

export function SafeguardsPanel({projectId}: {projectId: string}) {
  const safeguards = useSafeguards(projectId);
  const update = useUpdateSafeguards(projectId);
  if (!safeguards.data) return null;
  return (
    <AwPanel>
      <h2>Release Safeguards</h2>
      <p className="task-detail__muted">Gates enforced before a release can be promoted between environments.</p>
      <AwCheckbox id="safeguard-automated-tests" label="Require Automated Tests" checked={safeguards.data.requireAutomatedTests} onChange={event => update.mutate({requireAutomatedTests: event.target.checked})}/>
      <AwCheckbox id="safeguard-security-scan" label="Require Security Scan" checked={safeguards.data.requireSecurityScan} onChange={event => update.mutate({requireSecurityScan: event.target.checked})}/>
      <AwCheckbox id="safeguard-manual-approval" label="Require Manual Approval" checked={safeguards.data.requireManualApproval} onChange={event => update.mutate({requireManualApproval: event.target.checked})}/>
    </AwPanel>
  );
}

export function ReleaseLifecycleRail({stages, projectId}: {stages: Array<{environment: string; state: string; failureSummary?: string | null}>; projectId: string}) {
  const health = useEnvironmentHealth(projectId);
  const config = useEnvironmentChecks(projectId);
  const updateConfig = useUpdateEnvironmentCheck(projectId);
  const [configuring, setConfiguring] = useState<TaskEnvironment | null>(null);
  const current = config.data?.find(row => row.environment === configuring);

  async function submitConfig(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!configuring) return;
    const form = new FormData(event.currentTarget);
    await updateConfig.mutateAsync({
      environment: configuring,
      baseUrl: String(form.get('baseUrl')),
      healthPath: String(form.get('healthPath') || '/api/health'),
      enabled: Boolean(form.get('enabled')),
      intervalSeconds: Number(form.get('intervalSeconds') || 60),
      timeoutMs: Number(form.get('timeoutMs') || 5000)
    });
    setConfiguring(null);
  }

  const railStages: LifecycleStage[] = stages.map(stage => {
    const row = health.data?.find(item => item.environment === stage.environment);
    return {
      environment: stage.environment,
      state: stage.state as LifecycleStage['state'],
      detail: stage.failureSummary ?? stage.state,
      health: row ? {status: row.status, uptimePct: row.uptimePct, incidents7d: row.incidents7d} : undefined,
      onConfigureHealth: () => setConfiguring(stage.environment as TaskEnvironment)
    };
  });

  return (
    <>
      <AwLifecycleRail stages={railStages}/>
      <AwModal open={Boolean(configuring)} title={`Configure ${configuring ?? ''} health check`} onClose={() => setConfiguring(null)}>
        <form className="project-form" onSubmit={submitConfig}>
          <AwField id="env-check-base-url" name="baseUrl" label="Base URL" placeholder="https://game.example.com" defaultValue={current?.baseUrl ?? ''} required/>
          <AwField id="env-check-health-path" name="healthPath" label="Health Path" defaultValue={current?.healthPath ?? '/api/health'} required/>
          <div className="project-form__row">
            <AwField id="env-check-interval" name="intervalSeconds" label="Poll Interval (seconds)" type="number" min={15} max={3600} defaultValue={current?.intervalSeconds ?? 60}/>
            <AwField id="env-check-timeout" name="timeoutMs" label="Timeout (ms)" type="number" min={500} max={30000} defaultValue={current?.timeoutMs ?? 5000}/>
          </div>
          <AwCheckbox id="env-check-enabled" name="enabled" label="Enabled" defaultChecked={current?.configured ? current.enabled : true}/>
          <div className="project-form__actions">
            <AwButton type="button" variant="secondary" onClick={() => setConfiguring(null)}>Cancel</AwButton>
            <AwButton type="submit">Save</AwButton>
          </div>
        </form>
      </AwModal>
    </>
  );
}
